"use client";

import React, { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: 1,
  number: "01",
  title: "Resource Allocation",
  description: "Strategic deployment of human capital and technical assets across global markets. We optimize your operational footprint by ensuring the right expertise is positioned exactly where it's needed most."
}, {
  id: 2,
  number: "02",
  title: "Global Logistics",
  description: "End-to-end supply chain orchestration and international freight management. Our logistics experts navigate complex regulatory landscapes to ensure seamless movement of materials and equipment worldwide."
}, {
  id: 3,
  number: "03",
  title: "Strategic Scheduling",
  description: "Precision-engineered project timelines that account for multi-national variables and critical path dependencies. We synchronize complex workflows to maintain momentum and meet aggressive milestones."
}];
const serviceImages = [{
  id: 1,
  serviceId: 1,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/resource_allocation_hub.png",
  alt: "Global team coordinating resources in a modern control room"
}, {
  id: 2,
  serviceId: 1,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/resource_data_analysis.png",
  alt: "Digital interface showing resource distribution metrics"
}, {
  id: 3,
  serviceId: 1,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/resource_strategy_meeting.png",
  alt: "Consultants discussing strategic asset deployment"
}, {
  id: 4,
  serviceId: 2,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/global_logistics_port.png",
  alt: "High-shutter-speed shot of a global shipping terminal"
}, {
  id: 5,
  serviceId: 2,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/logistics_tracking_system.png",
  alt: "Technical display of real-time global freight tracking"
}, {
  id: 6,
  serviceId: 2,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/logistics_air_freight.png",
  alt: "Cargo aircraft being loaded for international transport"
}, {
  id: 7,
  serviceId: 3,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/strategic_scheduling_timeline.png",
  alt: "Complex project Gantt chart on a high-tech display"
}, {
  id: 8,
  serviceId: 3,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/scheduling_coordination_center.png",
  alt: "Project managers synchronizing international schedules"
}, {
  id: 9,
  serviceId: 3,
  url: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648747/img/scheduling_milestone_review.png",
  alt: "Team reviewing project milestones in a glass-walled office"
}];
interface ServiceBlockProps {
  service: (typeof services)[0];
  images: typeof serviceImages;
  index: number;
  "data-index": number;
}
function ServiceBlock({
  service,
  images,
  index,
  "data-index": dataIndex
}: ServiceBlockProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="232584" ref={ref} data-index={dataIndex} className={`relative flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} items-center gap-12 lg:gap-24 py-24 lg:py-32 border-b border-border/10 last:border-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className={`absolute top-10 ${isEven ? "left-0" : "right-0"} text-[12rem] lg:text-[20rem] font-mono font-bold text-primary opacity-5 select-none pointer-events-none leading-none z-0`}>
        {service.number}
      </div>

      {}
      <div className="w-full lg:w-1/2 relative z-10 flex flex-col items-start">
        <div className="flex items-center gap-4 mb-6">
          <span className="text-primary font-mono text-xl font-bold">
            {service.number}
          </span>
          <div className="h-px w-12 bg-primary shadow-[0_0_8px_rgba(0,184,255,0.5)]"></div>
        </div>
        <h3 className="font-default text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-6 text-foreground">
          {service.title}
        </h3>
        <p className="text-muted-foreground text-lg lg:text-xl leading-relaxed mb-10 max-w-xl font-default">
          {service.description}
        </p>
        <Button variant="outline" size="lg" className="rounded-none border-2 border-primary/50 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground font-mono uppercase tracking-widest transition-all duration-300 h-14 px-8 shadow-sm hover:shadow-primary/20" asChild>
          <Link to="#">
            <Download className="mr-2 size-5" />
            Technical Framework
          </Link>
        </Button>
      </div>

      {}
      <div className="w-full lg:w-1/2 grid grid-cols-2 gap-4 h-[500px] lg:h-[650px] relative z-10">
        {images[0] && <div className="col-span-1 row-span-2 h-full relative group overflow-hidden bg-card rounded-sm">
            <img src={images[0].url} alt={images[0].alt} className="absolute inset-0 w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 border border-white/5 pointer-events-none"></div>
            <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500"></div>
          </div>}
        {images[1] && <div className="col-span-1 row-span-1 h-full relative group overflow-hidden bg-card rounded-sm">
            <img src={images[1].url} alt={images[1].alt} className="absolute inset-0 w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 border border-white/5 pointer-events-none"></div>
            <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500"></div>
          </div>}
        {images[2] && <div className="col-span-1 row-span-1 h-full relative group overflow-hidden bg-card rounded-sm">
            <img src={images[2].url} alt={images[2].alt} className="absolute inset-0 w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 border border-white/5 pointer-events-none"></div>
            <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500"></div>
          </div>}
      </div>
    </div>;
}
export default function OurExpertise() {
  return <section id="strategic-execution-hub" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }}>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-16 lg:mb-24">
          <h2 className="font-default text-5xl lg:text-7xl font-bold uppercase tracking-tighter text-foreground mb-4">
            Strategic Execution
          </h2>
          <div className="h-1 w-24 bg-primary shadow-[0_0_12px_rgba(0,184,255,0.8)]"></div>
        </div>

        <div className="flex flex-col">
          {services.map((service, i) => {
          const blockImages = serviceImages.filter(img => img.serviceId === service.id);
          return <ServiceBlock key={service.id} data-index={i} index={i} service={service} images={blockImages} />;
        })}
        </div>
      </div>
    </section>;
}